import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import SnackbarUtils from "../../../libs/SnackbarUtils";
import { isEmail } from "../../../libs/RegexUtils";
import { serviceForgotPassword } from "../../../services/index.services";

const initialForm = {
  email: "",
};

const useForgotPasswordHook = () => {
  const history = useHistory();

  const [form, setForm] = useState(initialForm);
  const [errorData, setErrorData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkFormValidation = useCallback(() => {
    const errors = {};

    const email = form.email.trim();

    if (!email) {
      errors.email = true;
    } else if (!isEmail(email)) {
      errors.email = true;
    }

    return errors;
  }, [form.email]);

  const removeError = useCallback((field) => {
    setErrorData((prev) => {
      if (!prev[field]) return prev;

      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  }, []);

  const changeTextData = useCallback(
    (text, fieldName) => {
      setForm((prev) => ({
        ...prev,
        [fieldName]: text,
      }));

      removeError(fieldName);
    },
    [removeError]
  );

  const onBlurHandler = useCallback((field) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field]?.trim() || "",
    }));
  }, []);

  const submitToServer = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const email = form.email.trim();

      const payload = {
        email,
      };

      const response = await serviceForgotPassword(payload);

      // Check API response
      if (response.error) {
        SnackbarUtils.error(
          response.message || "Unable to send reset password link."
        );
        return;
      }

      SnackbarUtils.success(
        "Password reset link has been sent successfully."
      );

      setForm(initialForm);
      setErrorData({});

      history.push("/forgot-password-success", {
        email,
      });
    } catch (error) {
      SnackbarUtils.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [form.email, history]);

  const handleSubmit = useCallback(async () => {
    const errors = checkFormValidation();

    if (Object.keys(errors).length > 0) {
      setErrorData(errors);
      return;
    }

    await submitToServer();
  }, [checkFormValidation, submitToServer]);

  return {
    form,
    errorData,
    isSubmitting,
    changeTextData,
    onBlurHandler,
    handleSubmit,
  };
};

export default useForgotPasswordHook;